import { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/features/auth";
import { useDealsCart } from "@/features/deals/hooks/useDealsCart";
import type { DealCart } from "@/features/deals/types/dealCart.type";

type DealsCartContextValue = {
  cart: DealCart | undefined
  totalItems: number
  totalAmount: number
  isLoading: boolean
  isError: boolean
};

const DealsCartContext = createContext<DealsCartContextValue | undefined>(
  undefined,
);

export const DealsCartProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const {
    data: cart,
    isLoading,
    isError,
  } = useDealsCart({ enabled: isAuthenticated });

  const totalItems = useMemo(() => cart?.total_items ?? 0, [cart]);
  const totalAmount = useMemo(() => cart?.total_amount ?? 0, [cart]);

  const value = useMemo(
    () => ({
      cart,
      totalItems,
      totalAmount,
      isLoading,
      isError,
    }),
    [cart, totalItems, totalAmount, isLoading, isError],
  );

  return (
    <DealsCartContext.Provider value={value}>
      {children}
    </DealsCartContext.Provider>
  );
};

/**
 * Hook to access the deals cart context
 */
export const useDealsCartContext = (): DealsCartContextValue => {
  const context = useContext(DealsCartContext);

  if (!context) {
    return {
      cart: undefined,
      totalItems: 0,
      totalAmount: 0,
      isLoading: false,
      isError: true,
    };
  }

  return context;
};
